<template>
  <div
    class="py-6 px-6 bg-pure-white rounded-xl"
    :style="{ height: '27.5rem', width: '51.25rem' }"
  >
    <div class="mb-1 flex items-center justify-between">
      <BlTextTitle4 text="Client hours" class="text-slate-900" />
      <router-link
        :to="
          `/analytics/appointment-overview?start_date=${dateInput.model[0].start_date}&end_date=${dateInput.model[0].end_date}`
        "
      >
        <BlInputButton type="outlined" dense label="Details" />
      </router-link>
    </div>
    <div class="mb-5 flex items-center gap-2">
      <BlInputFilter
        :activeLabel="dateActiveLabel"
        title="Date"
        :defaultValue="[defaultDate]"
        :input="dateInput"
        @changed="onChangeHandler"
      />
    </div>
    <div class="mb-2">
      <BlTextSubtitle2 text="By total hours" class="text-slate-700" />
    </div>
    <div v-if="fetchLoading" class="h-72 grid place-content-center">
      <div class="blum-loader"></div>
    </div>
    <div
      v-else-if="barChartData.empty"
      class="h-72 grid flex-col place-content-center text-center"
    >
      <BlTextBody2 :text="emptyState.title" class="px-4 font-semibold" />
      <BlTextBody2
        v-for="(text, idx) in emptyState.desc"
        :key="idx"
        :text="text"
        class="px-4"
      />
    </div>
    <div v-else class="flex flex-col">
      <BarChart
        :labels="barChartData.labels"
        :datasets="barChartData.datasets"
        stacked
        width="calc(51.25rem - 3rem)"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import moment from "moment";

import BarChart from "../graphs/BarChart.vue";

import BlTextTitle4 from "../../BlTextTitle4.vue";
import BlTextSubtitle2 from "../../BlTextSubtitle2.vue";
import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlInputFilter from "../../BlInputFilter.vue";
import BlInputSelect from "../../BlInputSelect.vue";
import BlInputButton from "../../BlInputButton.vue";

export default {
  name: "CenterOverviewClientHours",
  components: {
    BarChart,
    BlTextTitle4,
    BlTextSubtitle2,
    BlTextSubtitle3,
    BlTextBody2,
    BlInputFilter,
    BlInputSelect,
    BlInputButton
  },
  data() {
    const interval = "week";
    const defaultDate = {
      start_date: moment()
        .startOf(interval)
        .format("YYYY-MM-DD"),
      end_date: moment()
        .endOf(interval)
        .format("YYYY-MM-DD")
    };

    return {
      defaultDate,
      /** */
      fetchLoading: false,
      graphData: null,
      /** */
      emptyState: {
        title: "No sessions recorded yet",
        desc: [
          "It looks like you've selected a future date range.",
          "Since no sessions have been completed yet, there's no data to display.",
          "Try selecting a date range that includes past or current dates to view your session history."
        ]
      },
      /** */
      dateInput: {
        model: [defaultDate],
        tabs: [{ name: "Date", kind: "date" }],
        dense: true
      }
    };
  },
  computed: {
    ...mapGetters(["currentUser"]),
    searchParams() {
      return this.$route.query || {};
    },
    dateActiveLabel() {
      const start = this.dateInput.model[0].start_date;
      const end = this.dateInput.model[0].end_date;

      const sYYYY = moment(start).format("YYYY");
      const sMMM = moment(start).format("MMM");
      const sDD = moment(start).format("DD");

      const eYYYY = moment(end).format("YYYY");
      const eMMM = moment(end).format("MMM");
      const eDD = moment(end).format("DD");

      let date = "";
      if (sYYYY === eYYYY) {
        if (sMMM === eMMM) date = `${sDD} - ${eDD} ${sMMM}, ${sYYYY}`;
        else if (sMMM === "Jan" && eMMM === "Dec") date = `${sYYYY}`;
        else date = `${sDD} ${sMMM} - ${eDD} ${eMMM}, ${sYYYY}`;
      } else date = `${sYYYY} - ${eYYYY}`;
      return `
        <span class="flex items-center gap-2">
          <span>Date</span>
          <b>${date}</b>
        </span>
      `;
    },
    /*
    "completed_session_duration": [
      {
        "duration": 0.5,
        "total": 5
      }
    ],
    */
    barChartData() {
      const n = { labels: [], datasets: [], legends: [], empty: true };
      if (!this.graphData) return n;
      const raw = this.graphData.completed_session_duration;
      if (!raw) return n;
      const keys = ["total"];
      const colors = ["#B9D843"];
      const texts = ["Completed session"];

      const labels = [];
      const datasets = keys.map((key, idx) => ({
        label: texts[idx],
        data: [],
        color: colors[idx]
      }));
      const legends = keys.map((key, idx) => ({
        label: texts[idx],
        color: colors[idx]
      }));
      let empty = true;

      raw.forEach(i => {
        const l = `${i.duration} hr`;
        labels.push(l);
        keys.forEach((key, idx) => {
          const value = i[key] || 0;
          datasets[idx].data.push(value);
          if (value) empty = false;
        });
      });

      return { labels, datasets, legends, empty };
    }
  },
  mounted() {
    this.fetchGraph();
  },
  methods: {
    ...mapActions(["actionGet"]),
    fetchGraph() {
      const start = this.dateInput.model[0].start_date;
      const end = this.dateInput.model[0].end_date;
      let params = `?start_date=${start}&end_date=${end}`;
      if (this.searchParams["branch"]) {
        params += `&branch_ids=${this.searchParams["branch"]}`;
      }
      const data = {
        url: `/api/v1/analytics/appointment_overview_graph${params}`
      };
      this.fetchLoading = true;
      this.graphData = null;
      this.actionGet(data).then(({ success, data }) => {
        this.fetchLoading = false;
        if (!success) return;
        this.graphData = data;
      });
    },
    onChangeHandler(payload) {
      payload.input.model = payload.value;
      this.fetchGraph();
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/analytics/mainDashboard/ClientHours.vue